import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import Marquee from "react-fast-marquee";
import axios from "axios";
import { decryptRates } from "../utils/crypto";

const API = process.env.REACT_APP_BACKEND_URL;

// Flag paths for fiat currencies
const getFiatFlag = (code) => {
  const flags = {
    USD: "/flags/USD.png",
    EUR: "/flags/EUR.png",
    GBP: "/flags/GBP.png",
    CAD: "/flags/CAD.png",
    MXN: "/flags/MXN.png",
    JPY: "/flags/JPY.png",
    AUD: "/flags/AUD.png",
    NZD: "/flags/NZD.png",
    CHF: "/flags/CHF.png",
    CNY: "/flags/CNY.png",
    INR: "/flags/INR.png",
    AED: "/flags/AED.png",
    BRL: "/flags/BRL.png",
    HKD: "/flags/HKD.png",
    SGD: "/flags/SGD.png",
    KRW: "/flags/KRW.png",
    THB: "/flags/THB.png",
    PHP: "/flags/PHP.png",
    TWD: "/flags/TWD.png",
    ZAR: "/flags/ZAR.png",
    IQD: "/flags/IQD.png",
    VND: "/flags/VND.png",
  };
  // Any other currency: use its file in /flags if there is one (hidden if missing)
  return flags[code] || (/^[A-Z]{3}$/.test(code || "") ? `/flags/${code}.png` : null);
};

// Crypto icons
const getCryptoIcon = (symbol) => {
  const icons = {
    BTC: "https://assets.coingecko.com/coins/images/1/small/bitcoin.png",
    ETH: "https://assets.coingecko.com/coins/images/279/small/ethereum.png",
    USDT: "https://assets.coingecko.com/coins/images/325/small/Tether.png",
    XRP: "https://assets.coingecko.com/coins/images/44/small/xrp-symbol-white-128.png",
    BNB: "https://assets.coingecko.com/coins/images/825/small/bnb-icon2_2x.png",
    SOL: "https://assets.coingecko.com/coins/images/4128/small/solana.png",
    DOGE: "https://assets.coingecko.com/coins/images/5/small/dogecoin.png",
    ADA: "https://assets.coingecko.com/coins/images/975/small/cardano.png",
    TRX: "https://assets.coingecko.com/coins/images/1094/small/tron-logo.png",
    AVAX: "https://assets.coingecko.com/coins/images/12559/small/Avalanche_Circle_RedWhite_Trans.png",
  };
  return icons[symbol] || null;
};

// Format price for display
const formatPrice = (price, isCrypto = false) => {
  if (isCrypto) {
    if (price >= 1000) return price.toLocaleString('en-CA', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    if (price >= 1) return price.toFixed(2);
    return price.toFixed(4);
  }
  // CAD per unit, same as the CRM rate board
  if (price < 1) return price.toFixed(6);
  return price.toFixed(4);
};

// Single marquee item component
const MarqueeItem = ({ rate, isCrypto }) => {
  if (isCrypto) {
    return (
      <div className="inline-flex items-center gap-3 px-6 whitespace-nowrap">
        {getCryptoIcon(rate.symbol) && (
          <img 
            src={getCryptoIcon(rate.symbol)} 
            alt={rate.symbol}
            className="w-5 h-5 rounded-full flex-shrink-0"
          />
        )}
        <span className="text-white font-semibold">{rate.symbol}</span>
        <span className={rate.change_24h >= 0 ? 'text-blue-400' : 'text-rose-400'}>
          ${formatPrice(rate.price_cad, true)}
        </span>
        <span className={`text-xs ${rate.change_24h >= 0 ? 'text-blue-400' : 'text-rose-400'}`}>
          {rate.change_24h >= 0 ? '+' : ''}{rate.change_24h?.toFixed(2) || '0.00'}%
        </span>
        <span className="text-white/20">|</span>
      </div>
    );
  }
  
  return (
    <div className="inline-flex items-center gap-3 px-6 whitespace-nowrap">
      {getFiatFlag(rate.currency_code) && (
        <img 
          src={getFiatFlag(rate.currency_code)} 
          alt={rate.currency_code}
          className="w-5 h-5 rounded-full object-cover flex-shrink-0"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      )}
      <span className="text-white font-semibold">{rate.currency_code}</span>
      <span className="text-emerald-400">
        We Buy: <span className="font-mono">{formatPrice(rate.sell_rate)}</span>
      </span>
      <span className="text-rose-400">
        We Sell: <span className="font-mono">{formatPrice(rate.buy_rate)}</span>
      </span>
      <span className="text-white/20">|</span>
    </div>
  );
};

export default function RateMarquee() {
  const [fiatRates, setFiatRates] = useState([]);
  const [cryptoRates, setCryptoRates] = useState([]);
  const location = useLocation();
  
  // Determine if we're on crypto page
  const isCryptoPage = location.pathname.startsWith('/crypto');

  // Fetch fiat rates
  const fetchFiatRates = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/x7k9`);
      const decryptedRates = decryptRates(response.data.d);
      // Filter only fiat (non-crypto) and exclude CAD
      const fiatOnly = decryptedRates.filter(r => 
        r.currency_code !== 'CAD' && 
        !['BTC', 'ETH', 'USDT', 'ADA', 'SOL', 'TRX'].includes(r.currency_code)
      );
      setFiatRates(fiatOnly);
    } catch (error) {
      console.error("Error fetching fiat rates:", error);
    }
  }, []);

  // Fetch crypto rates
  const fetchCryptoRates = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/crypto/prices`);
      setCryptoRates(response.data.prices || []);
    } catch (error) {
      console.error("Error fetching crypto rates:", error);
    }
  }, []);

  // Initial data fetch
  useEffect(() => {
    fetchFiatRates();
    fetchCryptoRates();
    const fiatInterval = setInterval(fetchFiatRates, 30000);
    const cryptoInterval = setInterval(fetchCryptoRates, 60000);
    return () => {
      clearInterval(fiatInterval);
      clearInterval(cryptoInterval);
    };
  }, [fetchFiatRates, fetchCryptoRates]);

  // Select which rates to show based on page
  const displayRates = isCryptoPage ? cryptoRates : fiatRates;
  
  if (displayRates.length === 0) return null;

  return (
    <div 
      className="bg-[#001435] border-b border-white/10 overflow-hidden sticky top-0 z-50"
      data-testid="rate-marquee"
    >
      <Marquee
        speed={40}
        gradient={false}
        pauseOnHover={true}
        className="py-2.5"
      >
        {displayRates.map((rate, index) => (
          <MarqueeItem 
            key={`${rate.id || rate.currency_code || rate.symbol}-${index}`} 
            rate={rate} 
            isCrypto={isCryptoPage} 
          />
        ))}
      </Marquee>
    </div>
  );
}
